import { apiClient as api } from "./httpClient";

/**
 * Formas de pagamento e contas bancárias da entidade (RN-217) — a mesma tela, duas abas, a
 * permissão `payment-origins`. A leitura traz os dois cadastros e o catálogo de bancos de uma vez:
 * a aba de contas precisa dos bancos e a de formas precisa das contas.
 */
export type PaymentOriginType = "Entrada" | "Saída";

export const PAYMENT_ORIGIN_TYPES: PaymentOriginType[] = ["Entrada", "Saída"];

export interface PaymentOriginRow {
  id: string;
  name: string;
  type: PaymentOriginType;
  entityAccountId: string | null;
  entityAccountName: string | null;
  active: boolean;
  usageCount: number;
}

export interface EntityAccountRow {
  id: string;
  title: string;
  description: string | null;
  bankId: string | null;
  bankName: string | null;
  number: string | null;
  digit: string | null;
  pixKey: string | null;
  active: boolean;
  originsCount: number;
}

export interface BankOption {
  id: string;
  name: string;
}

export interface PaymentOriginsData {
  origins: PaymentOriginRow[];
  accounts: EntityAccountRow[];
  banks: BankOption[];
}

export interface SavePaymentOriginPayload {
  id: string | null;
  name: string;
  type: PaymentOriginType;
  entityAccountId: string | null;
  active: boolean;
}

export interface SaveEntityAccountPayload {
  id: string | null;
  title: string;
  description: string | null;
  bankId: string | null;
  number: string | null;
  digit: string | null;
  pixKey: string | null;
  active: boolean;
}

export const paymentOriginsService = {
  async getAll(): Promise<PaymentOriginsData> {
    const response = await api.get("/payment-origins");
    return response.data;
  },
  async save(payload: SavePaymentOriginPayload): Promise<string> {
    const response = await api.post("/payment-origins", payload);
    return response.data.id;
  },
  async remove(id: string): Promise<void> {
    await api.delete(`/payment-origins/${id}`);
  },
  async saveAccount(payload: SaveEntityAccountPayload): Promise<string> {
    const response = await api.post("/entity-accounts", payload);
    return response.data.id;
  },
  async removeAccount(id: string): Promise<void> {
    await api.delete(`/entity-accounts/${id}`);
  },
};

/** "Itaú - Jurídica · conta 12345-6" — o que identifica a conta numa linha só. */
export const accountSummary = (account: EntityAccountRow): string => {
  const parts = [account.bankName ?? undefined];
  if (account.number) parts.push(`conta ${account.number}${account.digit ? `-${account.digit}` : ""}`);
  if (account.pixKey) parts.push(`PIX ${account.pixKey}`);
  const detail = parts.filter(Boolean).join(" · ");
  return detail || "—";
};
